import { DATE_FORMAT } from '@constants';
import { Chip, Typography } from '@gurupay/gp-ui-library';
import type { StatementProps } from '@hooks/useAllRequestedStatements';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { TFunction } from 'i18next';

import { NonBreakingDate } from '../../messages/NonBreakingDate';
import { isActive, STATUS_COLOR, STATUS_LABEL } from '../utils';
import DownloadCell from './DownloadCell';
import StatementsMobileItem from './StatementsMobileItem';

export const StatementsRenderers = (t: TFunction) => ({
	accountName: ({ account_iban, account_name, status, id }: StatementProps) => (
		<div className="flex flex-col" data-testid={`statement-${id}-account-name`}>
			<Typography
				className={clsx('font-bold', !isActive(status) && 'text-neutral-400')}
				level="small-text"
			>
				{account_iban}
			</Typography>
			<Typography
				className={clsx(
					'font-medium',
					!isActive(status) ? 'text-neutral-400' : 'text-neutral-600',
				)}
				level="small-text"
			>
				{account_name}
			</Typography>
		</div>
	),

	period: ({ date_from, date_to, status }: StatementProps) => (
		<Typography
			className={clsx(
				'uppercase font-medium flex w-space-16 whitespace-nowrap',
				!isActive(status) && 'text-neutral-400',
			)}
			level="small-text"
		>
			{format(date_from, DATE_FORMAT)} – {format(date_to, DATE_FORMAT)}
		</Typography>
	),

	fileType: ({ type, status }: StatementProps) => (
		<Typography
			className={clsx(
				'uppercase font-medium flex w-space-16',
				!isActive(status) && 'text-neutral-400',
			)}
			level="small-text"
		>
			{type}
		</Typography>
	),

	status: ({ status }: StatementProps) =>
		!!STATUS_LABEL[status] && (
			<Chip
				className="capitalize"
				color={STATUS_COLOR[status]}
				label={t(STATUS_LABEL[status])}
				size="medium"
				variant="filled"
			/>
		),

	date: ({ created_at, status }: StatementProps) => (
		<Typography
			className={clsx(
				'font-medium whitespace-nowrap left-space-4',
				!isActive(status) ? 'text-neutral-400' : 'text-neutral-800',
			)}
			level="small-text"
		>
			<NonBreakingDate date={created_at} />
		</Typography>
	),

	download: ({ status, account_uuid, account_id, id }: StatementProps) =>
		status === 'generated' && (
			<DownloadCell accountId={account_uuid ?? account_id} statementId={id} />
		),

	downloadStatements: ({
		status,
		created_at,
		type,
		account_uuid,
		id,
		account_iban,
		account_name,
		date_from,
		date_to,
	}: StatementProps) => (
		<StatementsMobileItem
			account_iban={account_iban}
			account_name={account_name}
			account_uuid={account_uuid}
			created_at={created_at}
			date_from={date_from}
			date_to={date_to}
			id={id}
			status={status}
			type={type}
		/>
	),
});
